/**
 * Wire format.
 *
 * ## Why the hot path is binary and nothing else is
 *
 * Exactly one message is sent every frame — the snapshot of where everybody is —
 * and it is the only one whose size is worth an encoder. At 20 Hz with eight
 * players, JSON costs roughly 1.6 kB per tick against 158 bytes packed, which over
 * a ten-minute match is 19 MB against 1.9 MB. On a phone on mobile data that
 * difference is the match.
 *
 * Everything else — a shot, a hit, a death, a score — is a handful of messages per
 * second at most, and a readable JSON payload is worth far more there than the
 * bytes it costs. A binary encoder for events would be a fixed schema to keep in
 * sync across two machines for a saving of nothing.
 *
 * ## Precision, chosen rather than defaulted
 *
 * Positions stay `float32`: the map is 104 m across and a 16-bit fixed-point
 * position would quantise to about 1.6 mm, which sounds fine and is not — the
 * error is in *world* space, so a player standing still jitters, and jitter reads
 * as a bad connection even on a perfect one.
 *
 * Angles are `int16` over ±π, which is 0.0055° per step. That is well below what
 * anyone can see on a remote player's body and a quarter the size of a float.
 */

export const KIND = { SNAPSHOT: 1, INPUT: 2 };

/** Flags packed into one byte of every transform. */
export const FLAG = {
  CROUCH: 1 << 0,
  ADS: 1 << 1,
  SPRINT: 1 << 2,
  DEAD: 1 << 3,
  FIRING: 1 << 4,
  GROUNDED: 1 << 5,
};

const ANGLE_SCALE = 32767 / Math.PI;

export const SNAPSHOT_HEADER = 6;
export const SNAPSHOT_PER_PLAYER = 19;
export const INPUT_BYTES = 22;

/**
 * @param {number} tick
 * @param {Array<{slot:number,x:number,y:number,z:number,yaw:number,pitch:number,flags:number,health:number}>} players
 */
export function encodeSnapshot(tick, players) {
  const buf = new ArrayBuffer(SNAPSHOT_HEADER + players.length * SNAPSHOT_PER_PLAYER);
  const v = new DataView(buf);
  let o = 0;
  v.setUint8(o, KIND.SNAPSHOT); o += 1;
  v.setUint32(o, tick >>> 0); o += 4;
  v.setUint8(o, players.length); o += 1;
  for (const p of players) {
    v.setUint8(o, p.slot); o += 1;
    v.setFloat32(o, p.x); o += 4;
    v.setFloat32(o, p.y); o += 4;
    v.setFloat32(o, p.z); o += 4;
    v.setInt16(o, clampAngle(p.yaw)); o += 2;
    v.setInt16(o, clampAngle(p.pitch)); o += 2;
    v.setUint8(o, p.flags & 0xff); o += 1;
    v.setUint8(o, Math.max(0, Math.min(255, Math.round(p.health)))); o += 1;
  }
  return buf;
}

export function decodeSnapshot(buf) {
  const v = new DataView(buf);
  let o = 0;
  if (v.getUint8(o) !== KIND.SNAPSHOT) return null;
  o += 1;
  const tick = v.getUint32(o); o += 4;
  const n = v.getUint8(o); o += 1;
  const players = [];
  for (let i = 0; i < n; i++) {
    const slot = v.getUint8(o); o += 1;
    const x = v.getFloat32(o); o += 4;
    const y = v.getFloat32(o); o += 4;
    const z = v.getFloat32(o); o += 4;
    const yaw = v.getInt16(o) / ANGLE_SCALE; o += 2;
    const pitch = v.getInt16(o) / ANGLE_SCALE; o += 2;
    const flags = v.getUint8(o); o += 1;
    const health = v.getUint8(o); o += 1;
    players.push({ slot, x, y, z, yaw, pitch, flags, health });
  }
  return { tick, players };
}

export function encodeInput(tick, p) {
  const buf = new ArrayBuffer(INPUT_BYTES);
  const v = new DataView(buf);
  let o = 0;
  v.setUint8(o, KIND.INPUT); o += 1;
  v.setUint32(o, tick >>> 0); o += 4;
  v.setFloat32(o, p.x); o += 4;
  v.setFloat32(o, p.y); o += 4;
  v.setFloat32(o, p.z); o += 4;
  v.setInt16(o, clampAngle(p.yaw)); o += 2;
  v.setInt16(o, clampAngle(p.pitch)); o += 2;
  v.setUint8(o, p.flags & 0xff); o += 1;
  return buf;
}

export function decodeInput(buf) {
  const v = new DataView(buf);
  let o = 0;
  if (v.getUint8(o) !== KIND.INPUT) return null;
  o += 1;
  const tick = v.getUint32(o); o += 4;
  const x = v.getFloat32(o); o += 4;
  const y = v.getFloat32(o); o += 4;
  const z = v.getFloat32(o); o += 4;
  const yaw = v.getInt16(o) / ANGLE_SCALE; o += 2;
  const pitch = v.getInt16(o) / ANGLE_SCALE; o += 2;
  const flags = v.getUint8(o); o += 1;
  return { tick, x, y, z, yaw, pitch, flags };
}

/**
 * Wrap into ±π before quantising.
 *
 * Yaw accumulates without bound as a player spins — nothing in the movement code
 * normalises it, because locally it never matters. Quantising an unwrapped 40 rad
 * silently overflows int16 and the remote body snaps to a random facing every few
 * turns, which is a bug that only appears after a minute of play.
 */
function clampAngle(a) {
  let x = a % (Math.PI * 2);
  if (x > Math.PI) x -= Math.PI * 2;
  else if (x < -Math.PI) x += Math.PI * 2;
  return Math.max(-32767, Math.min(32767, Math.round(x * ANGLE_SCALE)));
}
